import type { AppNotification, NotificationActor } from "@/features/notifications/types";

export function actorName(actor: Pick<NotificationActor, "firstName" | "lastName" | "emailAddress">): string {
  if (actor.firstName || actor.lastName) {
    return `${actor.firstName ?? ""} ${actor.lastName ?? ""}`.trim();
  }
  return actor.emailAddress;
}

export function actorInitials(actor: Pick<NotificationActor, "firstName" | "lastName" | "emailAddress">): string {
  if (actor.firstName && actor.lastName) return `${actor.firstName[0]}${actor.lastName[0]}`.toUpperCase();
  if (actor.firstName) return actor.firstName[0].toUpperCase();
  return actor.emailAddress[0].toUpperCase();
}

export function isUnread(n: AppNotification): boolean {
  return n.status === "Unread";
}

export function buildMessage(n: AppNotification): React.ReactNode {
  if (n.type === "ProjectInvitation" && n.invitationData) {
    const { invitationData: d } = n;
    return (
      <>
        <span className="font-semibold text-foreground">{actorName(d.sentBy)}</span>
        <span className="text-muted-foreground"> invited you to </span>
        <span className="font-semibold text-foreground">{d.project.name}</span>
        <span className="text-muted-foreground"> as </span>
        <span className="font-semibold text-foreground">{d.role.name}</span>
      </>
    );
  }

  if (n.type === "ProjectRemoval" && n.projectRemovalData) {
    const { projectRemovalData: d } = n;
    return (
      <>
        <span className="font-semibold text-foreground">{actorName(d.removedBy)}</span>
        <span className="text-muted-foreground"> removed you from </span>
        <span className="font-semibold text-foreground">{d.project.name}</span>
      </>
    );
  }

  if (n.type === "ProjectRoleUpdate" && n.projectRoleUpdateData) {
    const { projectRoleUpdateData: d } = n;
    return (
      <>
        <span className="font-semibold text-foreground">{actorName(d.updatedBy)}</span>
        <span className="text-muted-foreground"> updated your role in </span>
        <span className="font-semibold text-foreground">{d.project.name}</span>
        <span className="text-muted-foreground"> to </span>
        <span className="font-semibold text-foreground">{d.role.name}</span>
      </>
    );
  }

  if (n.type === "TicketAssignment" && n.ticketAssignmentData) {
    const { ticketAssignmentData: d } = n;
    return (
      <>
        <span className="font-semibold text-foreground">{actorName(d.assignedBy)}</span>
        <span className="text-muted-foreground"> assigned you to </span>
        {d.ticket ? (
          <span className="font-semibold text-foreground">
            {d.ticket.key} - {d.ticket.title}
          </span>
        ) : (
          <span className="italic text-muted-foreground">a deleted ticket</span>
        )}
        <span className="text-muted-foreground"> in </span>
        <span className="font-semibold text-foreground">{d.project.name}</span>
      </>
    );
  }

  if (n.type === "TicketUpdate" && n.ticketUpdateData) {
    const { ticketUpdateData: d } = n;
    return (
      <>
        <span className="font-semibold text-foreground">{actorName(d.updatedBy)}</span>
        <span className="text-muted-foreground"> updated </span>
        {d.ticket ? (
          <span className="font-semibold text-foreground">
            {d.ticket.key} - {d.ticket.title}
          </span>
        ) : (
          <span className="italic text-muted-foreground">a deleted ticket</span>
        )}
        <span className="text-muted-foreground"> in </span>
        <span className="font-semibold text-foreground">{d.project.name}</span>
      </>
    );
  }

  if (n.type === "CommentReply" && n.commentReplyData) {
    const { commentReplyData: d } = n;
    return (
      <>
        <span className="font-semibold text-foreground">{actorName(d.repliedBy)}</span>
        <span className="text-muted-foreground"> replied to your comment on </span>
        {d.ticket ? (
          <span className="font-semibold text-foreground">
            {d.ticket.key} - {d.ticket.title}
          </span>
        ) : (
          <span className="italic text-muted-foreground">a deleted ticket</span>
        )}
        <span className="text-muted-foreground"> in </span>
        <span className="font-semibold text-foreground">{d.project.name}</span>
      </>
    );
  }

  return <span className="text-muted-foreground">New notification</span>;
}

/** Trims a reply down to a single line preview for the notification card. */
export function commentExcerpt(body: string, maxLength = 140): string {
  const collapsed = body.replace(/\s+/g, " ").trim();

  return collapsed.length > maxLength ? `${collapsed.slice(0, maxLength).trimEnd()}…` : collapsed;
}
